import React, { memo } from "react"
// Import necessary components and icons
import { HiBookOpen } from "react-icons/hi"
import { IoHandLeftSharp } from "react-icons/io5"
import { useNavigate } from "react-router-dom"

import BottomAction from "@fuse/sharedComponents/buttons/BottomAction"
import SolidBtn from "@fuse/sharedComponents/buttons/SolidBtn"
import MapWebView from "@fuse/sharedComponents/map"
import { EventDetails } from "@fuse/types/event"
import {
  EButtonType,
  EEventStatus,
  EVENT_CLASS,
  EVENT_STATUS,
  EventDocData,
  TEventDetail,
} from "@letshang/constants"
import { get, isEmpty } from "lodash-es"
import { useRecoilValue } from "recoil"
import { ROUTES } from "src/App"

import { userState } from "@fuse/recoil/signup"

import RenderEventDescription, {
  RenderEventDescriptionViaHtml,
} from "@fuse/pages/eventPage/RenderEventDescription"

import First from "@fuse/assets/images/img-event-first.svg"

import AvatarGroup from "../components/AvatarGroup"

// Helper component for status tags
const Tag = ({ title }: { title: string }) => (
  <div className="bg-[red] text-white rounded-full py-2 px-4 text-sm font-semibold ">
    {title}
  </div>
)

// 1. Event Image Section
interface EventImageProps {
  flyerImage?: string
  isGuestView?: boolean
  children?: React.ReactNode
}

export const EventImage = memo<EventImageProps>(
  ({ flyerImage, isGuestView, children }) => {
    return (
      <div className="w-6/12 mx-auto lg:pr-6 mb-6 lg:mb-0 ">
        <div className=" px-4  md:max-w-md lg:max-w-lg ">
          {!isEmpty(flyerImage) ? (
            <img
              src={flyerImage}
              className=" w-full rounded-2xl"
              alt="Event Flyer"
            />
          ) : (
            <img src={First} className=" w-full lg:p-0" alt="Default Event" />
          )}

          {/* Buttons for desktop view - both guest and admin/host */}
          {children && (
            <div className="mt-4 hidden landscape:block w-full">{children}</div>
          )}
        </div>
      </div>
    )
  },
)

// 2. Event Header Section
interface EventHeaderProps {
  eventDocData?: EventDocData
  hasNotJoined?: boolean
  eventStatus?: string
  eventStatusData: EEventStatus
}

export const EventHeader = memo<EventHeaderProps>(
  ({ eventDocData, hasNotJoined, eventStatus, eventStatusData }) => {
    const user = useRecoilValue(userState)
    const hostProfilePic = get(eventDocData, "initiatorAvatarUrl", "")
    return (
      <>
        <div className=" flex gap-4 items-center">
          <h5 className="text-3xl md:text-4xl text-start text-white ">
            {get(eventDocData, "eventName", "-")}
          </h5>
          {!hasNotJoined && eventStatus === EEventStatus.CLOSED && (
            <Tag title={"Closed"} />
          )}
          {eventStatusData === EEventStatus.CLOSE && (
            <Tag title={"Cancelled"} />
          )}
        </div>
        <div className="flex flex-row mt-2 items-center">
          {!hostProfilePic?.includes("example.com") && (
            <div className="h-6 w-6 rounded-full bg-gray-400 overflow-hidden mr-2 sm:hidden">
              <img
                src={hostProfilePic}
                alt="profile"
                className="w-full h-full"
              />
            </div>
          )}
          <h5 className="text-start text-white/35">
            by {get(eventDocData, "initiatorName", "-")}
          </h5>
        </div>
      </>
    )
  },
)

// 3. Admin Notice Section
interface AdminNoticeProps {
  isAdmin?: boolean
  onDownloadAppClick: () => void
  isHidden?: boolean
}

export const AdminNotice = memo<AdminNoticeProps>(
  ({ isAdmin, onDownloadAppClick, isHidden: isHidden }) => {
    // Removed download app banner - no longer encouraging app downloads
    return null
  },
)

// 4. Draft Event Notice Section
interface DraftEventNoticeProps {
  isAdmin?: boolean
  isDraftEvent?: boolean
}

export const DraftEventNotice = memo<DraftEventNoticeProps>(
  ({ isAdmin, isDraftEvent }) => {
    if (isAdmin || !isDraftEvent) return null

    return (
      <div className="flex mt-6">
        <div className="inline-block bg-white/10 border-white/10 py-2 px-4 border rounded-lg">
          <p className="text-white/80 text-start text-sm">
            ⚠️ This event is in draft mode. Tell the host to publish it to be
            able to join.
          </p>
        </div>
      </div>
    )
  },
)

// 5. Interest Section
interface InterestSectionProps {
  isDraftEvent?: boolean
  eventDocData?: EventDocData
  isInterested: boolean
  onInterestedClick: () => void
}

export const InterestSection = memo<InterestSectionProps>(
  ({ isDraftEvent, eventDocData, isInterested, onInterestedClick }) => {
    if (!isDraftEvent || !eventDocData?.subscribeEvent?.enabled) return null

    return (
      <div className="flex w-full mt-6">
        <div className="flex-1 py-2 px-4 rounded-lg flex items-center justify-between bg-white/10 border-white/10 border">
          <p className="text-white text-start text-sm">
            👍{" "}
            {eventDocData?.subscribeEvent?.interestedCount > 1
              ? `${eventDocData?.subscribeEvent?.interestedCount} people are`
              : "Many people are"}{" "}
            interested in this event
          </p>

          <SolidBtn
            size="sm"
            onClick={isInterested ? () => {} : onInterestedClick}
            className="inline-flex text-white opacity-100 px-[20px] w-full sm:w-auto"
          >
            {isInterested ? "🤩 Interested" : "🤔 Interested?"}
          </SolidBtn>
        </div>
      </div>
    )
  },
)

// 6. Event Timing Section
interface EventTimingProps {
  displayDateRange: string
  eventStartTime: string
  eventEndTime: string
  onCalendarClick: () => void
}

export const EventTiming = memo<EventTimingProps>(
  ({ displayDateRange, eventStartTime, eventEndTime, onCalendarClick }) => {
    return (
      <div
        className="flex flex-row mb-6 cursor-pointer"
        onClick={onCalendarClick}
      >
        <div className="flex text-center items-center">
          <p className="text-3xl">🗓️</p>
        </div>
        <div className="flex-col ml-3">
          <h5 className="text-start text-white">{displayDateRange}</h5>
          <h5 className="text-start text-white/35 text-sm font-medium">
            {eventStartTime} - {eventEndTime}
          </h5>
        </div>
      </div>
    )
  },
)

// 7. Event Location Section
interface EventLocationProps {
  eventDocData?: EventDocData
  hasJoined: boolean
  isLocationHidden: boolean
  locationInfo: string
  latitude: number
  longitude: number
}

export const EventLocation = memo<EventLocationProps>(
  ({
    eventDocData,
    hasJoined,
    isLocationHidden,
    locationInfo,
    latitude,
    longitude,
  }) => {
    return (
      <div
        className={`flex flex-row mb-6 cursor-pointer ${hasJoined ? "pointer-events-auto" : "pointer-events-none"}`}
        onClick={() => {
          !isLocationHidden
            ? window.open(`https://maps.google.com?q=${latitude},${longitude}`)
            : null
        }}
      >
        <div className="flex text-center items-center">
          <p className="text-2xl">📍</p>
        </div>
        <div className="flex-col ml-4">
          <h5 className="text-start text-white">
            {get(eventDocData, "location.guestLocationPreview", "Hidden") ||
              "Hidden"}
          </h5>
          <h5 className="text-start text-white/35 text-sm font-medium">
            {locationInfo}
          </h5>
        </div>
      </div>
    )
  },
)

// 8. Event Pricing Section
interface EventPricingProps {
  isTicketedEvent: boolean
  ticketLeftString: string
  minTicketPrice: number
  maxTicketPrice: number
  isSingleTicket: boolean
  onTicketClick: () => void
}

export const EventPricing = memo<EventPricingProps>(
  ({
    isTicketedEvent,
    ticketLeftString,
    minTicketPrice,
    maxTicketPrice,
    isSingleTicket,
    onTicketClick,
  }) => {
    if (!isTicketedEvent) return null

    return (
      <>
        <div
          className="flex flex-row mb-6 cursor-pointer"
          onClick={onTicketClick}
        >
          <div className="flex text-center items-center">
            <p className="text-3xl">️🎟️</p>
          </div>
          <div className="flex-col ml-4">
            <h5 className="text-start text-white">{ticketLeftString}</h5>
          </div>
        </div>

        <div className="flex flex-row items-center" onClick={onTicketClick}>
          <div className="flex text-center items-center">
            <p className="text-3xl">💵</p>
          </div>
          <div className="flex-col ml-4 items-center">
            <h5 className="text-center text-white">
              {isSingleTicket
                ? `$${minTicketPrice}`
                : `$${minTicketPrice} - $${maxTicketPrice}`}
            </h5>
          </div>
        </div>
      </>
    )
  },
)

// 9. Event Join Info Section
interface EventJoinInfoProps {
  isTicketedEvent: boolean
  eventDocData?: EventDocData
  signUpCloseDate: string
  signUpCloseTime: string
}

export const EventJoinInfo = memo<EventJoinInfoProps>(
  ({ isTicketedEvent, eventDocData, signUpCloseDate, signUpCloseTime }) => {
    if (isTicketedEvent) return null

    return (
      <div className="flex flex-row mb-6">
        <div className="flex text-center items-center">
          <p className="text-3xl">✅</p>
        </div>
        <div className="flex-col ml-3">
          <h5 className="text-start text-white">
            {`${get(eventDocData, "numberAttending", 0)} joined`}
          </h5>
          <h5 className="text-start text-white/35 text-sm">
            Sign up closes on {signUpCloseDate} at {signUpCloseTime}
          </h5>
        </div>
      </div>
    )
  },
)

// 10. Event Actions Section
interface EventActionsProps {
  canLeaveEvent?: boolean
  onLeaveEvent?: () => void
}

export const EventActions = memo<EventActionsProps>(
  ({ canLeaveEvent, onLeaveEvent }) => {
    if (!canLeaveEvent) return null

    return (
      <div
        className="flex flex-row cursor-pointer mt-6"
        onClick={() => onLeaveEvent?.()}
      >
        <div className="flex text-center items-center">
          <p className="text-3xl">👋</p>
        </div>
        <div className="flex-col ml-4">
          <h5 className="text-start text-white underline">Leave event</h5>
          <h5 className="text-start text-white/35 text-sm">
            Click here to leave event
          </h5>
        </div>
      </div>
    )
  },
)

// 11. Event Description Section
interface EventDescriptionProps {
  eventDescription: string
  eventDescriptionViaHtml: string
  isAdmin?: boolean
}

export const EventDescription = memo<EventDescriptionProps>(
  ({ eventDescription, eventDescriptionViaHtml, isAdmin }) => {
    return (
      <div className="bg-white/10 border-white/20 border px-4 py-3 rounded-2xl">
        <h6 className="text-lg sm:text-xl text-start text-white">
          About this event
        </h6>
        {eventDescriptionViaHtml ? (
          <RenderEventDescriptionViaHtml
            eventDescriptionViaHtml={eventDescriptionViaHtml}
          />
        ) : (
          <h6
            className={`text-md text-start mt-1 ${!isAdmin ? "text-white/80" : "text-white/80 sm:text-white/35"}`}
          >
            <div>
              <RenderEventDescription eventDescription={eventDescription} />
            </div>
          </h6>
        )}
      </div>
    )
  },
)

// 12. Event Social Section
interface EventSocialSectionProps {
  isBuddiesEnabled: boolean
  acceptedGuestAvatars?: any[]
  isAdmin?: boolean
  eventDocData?: any
  onGuestListClick: () => void
  onAdminDownloadClick?: () => void
  eventDetails: TEventDetail
  isGuestView?: boolean
}

export const EventSocialSection = memo<EventSocialSectionProps>(
  ({
    isBuddiesEnabled,
    acceptedGuestAvatars,
    isAdmin,
    eventDocData,
    onGuestListClick,
    onAdminDownloadClick,
    eventDetails,
    isGuestView,
  }) => {
    if (!isBuddiesEnabled || !acceptedGuestAvatars?.length) return null

    const handleClick = () => {
      // if (!isAdmin) {
      onGuestListClick()
      // } else {
      //   onAdminDownloadClick?.()
      // }
    }

    const totalNumberOfPossibleAvatars =
      eventDocData.eventClass === EVENT_CLASS.NON_TICKETED_EVENT
        ? get(eventDocData, "numberAttending", 0)
        : get(eventDetails, "totalPurchasedTickets", 0)

    if (totalNumberOfPossibleAvatars === 0) return null

    return (
      <div className="mt-6 sm:mt-0 sm:mb-6 bg-white/10 border-white/20 border p-4 rounded-2xl">
        <h6 className="text-lg sm:text-xl text-start text-white">Guest List</h6>
        <div
          className="active:opacity-70 justify-items-start mt-2 cursor-pointer"
          onClick={handleClick}
        >
          <AvatarGroup
            data={acceptedGuestAvatars}
            maxVisible={4}
            avatarSize={46}
            totalNumberOfPossibleAvatars={totalNumberOfPossibleAvatars}
            borderColor={"#ffffff59"}
          />
        </div>
      </div>
    )
  },
)

// 13. Event Map Section
interface EventMapProps {
  hasJoined: boolean
  eventDocData?: EventDocData
  isAdmin?: boolean
}

export const EventMap = memo<EventMapProps>(
  ({ hasJoined, eventDocData, isAdmin }) => {
    if (!hasJoined) return null

    return (
      <div className="mt-6 bg-white/10 border-white/20 border px-4 py-3 rounded-2xl">
        <h6 className="text-lg sm:text-xl text-start text-white mb-3">
          Find this event
        </h6>
        <MapWebView
          isEventDetailView={true}
          coordinate={{
            lat: eventDocData?.location?.latitude,
            lng: eventDocData?.location?.longitude,
          }}
          isAdmin={isAdmin}
        />
      </div>
    )
  },
)

// 14. Event Bottom Actions Section
interface EventBottomActionsProps {
  myEventActions?: any[]
  onEventAction?: (index: number) => void
  isAdmin?: boolean
  eventDocData?: any
  showEdit?: boolean
  onOpenSettingsBottomSheet?: () => void
}

export const EventBottomActions = memo<EventBottomActionsProps>(
  ({
    myEventActions,
    onEventAction,
    isAdmin,
    eventDocData,
    showEdit,
    onOpenSettingsBottomSheet,
  }) => {
    return (
      <div className="portrait:hidden flex flex-row gap-4">
        {!isEmpty(myEventActions) && (
          <BottomAction
            bottomActionClassName="mt-6"
            buttonOptionList={myEventActions}
            handleScreen={onEventAction}
          />
        )}
        {isAdmin &&
          eventDocData?.eventStatus === EVENT_STATUS.OPEN &&
          showEdit && (
            <div className={`flex-shrink-0 w-full sm:w-auto mt-6`}>
              <SolidBtn
                size="lg"
                variant={EButtonType.solid}
                disabled={false}
                onClick={onOpenSettingsBottomSheet}
                className={`inline-flex text-white opacity-100 px-10 min-w-48 w-full sm:w-auto flex-1`}
              >
                {"More"}
              </SolidBtn>
            </div>
          )}
      </div>
    )
  },
)

// 15. Event Footer Section for Guest Desktop View
interface EventFooterSectionProps {
  isGuestView?: boolean
  isAdmin?: boolean
}

export const EventFooterSection = memo<EventFooterSectionProps>(
  ({ isGuestView, isAdmin }) => {
    const navigate = useNavigate()

    const handleButtonClick = (url: string) => {
      window.open(url, "_blank")
    }

    const onCreateEvent = () => {
      navigate(ROUTES.CREATE_EVENT_V5)
    }

    return (
      <div className="mt-6 bg-white/10 border-white/20 border p-4 rounded-2xl hidden lg:block">
        <div
          className="text-4xl min-w-max font-bold leading-6 drop-shadow-lg flex items-center justify-center mb-8 mt-3 text-white"
          style={{
            fontFamily: "Syne, sans-serif",
            textShadow: "0px 0px 10px white",
          }}
        >
          <p className="font-syne">
            let's
            <br />
            <span className="mt-2 block">hang</span>
          </p>
        </div>

        <div className="flex flex-col items-start gap-2 my-4">
          <button
            onClick={() =>
              handleButtonClick("https://pages.flycricket.io/walkme/terms.html")
            }
            className="text-left text-base sm:text-sm font-medium bg-transparent flex gap-2 justify-start items-center p-0 w-full"
          >
            <HiBookOpen size={18} fill={"#FFFFFF99"} />
            <div className="text-nowrap text-white/80">TERMS OF SERVICE</div>
          </button>

          <button
            onClick={() =>
              handleButtonClick(
                "https://pages.flycricket.io/walkme/privacy.html",
              )
            }
            className="text-left text-base sm:text-sm font-medium bg-transparent flex gap-2 justify-start items-center p-0 w-full"
          >
            <IoHandLeftSharp size={18} fill={"#FFFFFF99"} />
            <div className="text-nowrap text-white/80">PRIVACY POLICY</div>
          </button>
        </div>

        <div
          onClick={onCreateEvent}
          className="w-full flex items-center justify-center text-lg text-white/80 cursor-pointer bg-white/20 py-2 rounded-lg"
        >
          {isAdmin ? "Create another event" : "Create your own event"}
        </div>
      </div>
    )
  },
)
